import { apply, Matrix, Point } from "./matrix";

// Normalised path: absolute coordinates, only M / L / C / Z.
export type Segment =
  | { readonly type: "M"; readonly p: Point }
  | { readonly type: "L"; readonly p: Point }
  | { readonly type: "C"; readonly c1: Point; readonly c2: Point; readonly p: Point }
  | { readonly type: "Z" };

export interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

const ARG_COUNTS: Readonly<Record<string, number>> = { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2, A: 7, Z: 0 };
const TOKEN_RE = /([MLHVCSQTAZmlhvcsqtaz])|([-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?)/g;

interface Command {
  readonly cmd: string;
  readonly args: number[];
}

const tokenize = (d: string): Command[] => {
  const commands: Command[] = [];
  let current: Command | null = null;
  TOKEN_RE.lastIndex = 0;
  for (let match = TOKEN_RE.exec(d); match; match = TOKEN_RE.exec(d)) {
    if (match[1]) {
      current = { cmd: match[1], args: [] };
      commands.push(current);
    } else if (current) {
      const upper = current.cmd.toUpperCase();
      // Implicit repetition: extra arguments start a new command of the same kind (M repeats as L).
      if (current.args.length === ARG_COUNTS[upper] && upper !== "Z") {
        const repeated = upper === "M" ? (current.cmd === "m" ? "l" : "L") : current.cmd;
        current = { cmd: repeated, args: [] };
        commands.push(current);
      }
      // Arc flags are single digits and may be packed ("a10 10 0 01 20 20"): take one char.
      const isArcFlag = upper === "A" && (current.args.length === 3 || current.args.length === 4);
      if (isArcFlag && /^[01]/.test(match[2]) && match[2].length > 1) {
        current.args.push(Number(match[2][0]));
        TOKEN_RE.lastIndex = match.index + 1;
        continue;
      }
      current.args.push(Number(match[2]));
    }
  }
  return commands.filter((c) => c.args.length === ARG_COUNTS[c.cmd.toUpperCase()]);
};

const quadToCubic = (from: Point, q: Point, to: Point): Segment => ({
  type: "C",
  c1: { x: from.x + (2 / 3) * (q.x - from.x), y: from.y + (2 / 3) * (q.y - from.y) },
  c2: { x: to.x + (2 / 3) * (q.x - to.x), y: to.y + (2 / 3) * (q.y - to.y) },
  p: to,
});

const vectorAngle = (ux: number, uy: number, vx: number, vy: number): number => {
  const sign = ux * vy - uy * vx < 0 ? -1 : 1;
  const dot = (ux * vx + uy * vy) / (Math.hypot(ux, uy) * Math.hypot(vx, vy));
  return sign * Math.acos(Math.max(-1, Math.min(1, dot)));
};

// Endpoint-to-centre arc conversion (SVG spec F.6), split into <= 90° cubic pieces.
const arcToCubics = (from: Point, args: number[], to: Point): Segment[] => {
  let [rx, ry] = [Math.abs(args[0]), Math.abs(args[1])];
  const [, , angleDeg, largeArc, sweep] = args;
  if (rx === 0 || ry === 0 || (from.x === to.x && from.y === to.y)) return [{ type: "L", p: to }];
  const phi = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);
  const dx = (from.x - to.x) / 2;
  const dy = (from.y - to.y) / 2;
  const x1 = cos * dx + sin * dy;
  const y1 = -sin * dx + cos * dy;
  const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const num = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
  const den = rx * rx * y1 * y1 + ry * ry * x1 * x1;
  const coef = (largeArc !== sweep ? 1 : -1) * Math.sqrt(Math.max(0, num / den));
  const cx1 = (coef * rx * y1) / ry;
  const cy1 = (-coef * ry * x1) / rx;
  const cx = cos * cx1 - sin * cy1 + (from.x + to.x) / 2;
  const cy = sin * cx1 + cos * cy1 + (from.y + to.y) / 2;
  const theta1 = vectorAngle(1, 0, (x1 - cx1) / rx, (y1 - cy1) / ry);
  let delta = vectorAngle((x1 - cx1) / rx, (y1 - cy1) / ry, (-x1 - cx1) / rx, (-y1 - cy1) / ry);
  if (!sweep && delta > 0) delta -= 2 * Math.PI;
  if (sweep && delta < 0) delta += 2 * Math.PI;

  const pieces = Math.max(1, Math.ceil(Math.abs(delta) / (Math.PI / 2)));
  const step = delta / pieces;
  const k = (4 / 3) * Math.tan(step / 4);
  const point = (t: number, rxs: number, rys: number): Point => ({
    x: cx + rxs * Math.cos(t) * cos - rys * Math.sin(t) * sin,
    y: cy + rxs * Math.cos(t) * sin + rys * Math.sin(t) * cos,
  });
  const derivative = (t: number): Point => ({
    x: -rx * Math.sin(t) * cos - ry * Math.cos(t) * sin,
    y: -rx * Math.sin(t) * sin + ry * Math.cos(t) * cos,
  });
  const segments: Segment[] = [];
  for (let i = 0; i < pieces; i += 1) {
    const t1 = theta1 + i * step;
    const t2 = t1 + step;
    const p1 = point(t1, rx, ry);
    const p2 = i === pieces - 1 ? to : point(t2, rx, ry);
    const d1 = derivative(t1);
    const d2 = derivative(t2);
    segments.push({
      type: "C",
      c1: { x: p1.x + k * d1.x, y: p1.y + k * d1.y },
      c2: { x: p2.x - k * d2.x, y: p2.y - k * d2.y },
      p: p2,
    });
  }
  return segments;
};

/** Parses path data into absolute M/L/C/Z segments. Malformed tails are dropped, never thrown. */
export const parsePath = (d: string): Segment[] => {
  const out: Segment[] = [];
  let cur: Point = { x: 0, y: 0 };
  let start: Point = cur;
  let lastCubicCtrl: Point | null = null;
  let lastQuadCtrl: Point | null = null;

  for (const { cmd, args } of tokenize(d)) {
    const rel = cmd === cmd.toLowerCase();
    const upper = cmd.toUpperCase();
    const pt = (x: number, y: number): Point => (rel ? { x: cur.x + x, y: cur.y + y } : { x, y });
    let nextCubic: Point | null = null;
    let nextQuad: Point | null = null;

    switch (upper) {
      case "M":
        cur = pt(args[0], args[1]);
        start = cur;
        out.push({ type: "M", p: cur });
        break;
      case "L":
      case "H":
      case "V": {
        const p =
          upper === "L"
            ? pt(args[0], args[1])
            : upper === "H"
            ? { x: rel ? cur.x + args[0] : args[0], y: cur.y }
            : { x: cur.x, y: rel ? cur.y + args[0] : args[0] };
        out.push({ type: "L", p });
        cur = p;
        break;
      }
      case "C":
      case "S": {
        const c1 =
          upper === "C"
            ? pt(args[0], args[1])
            : lastCubicCtrl
            ? { x: 2 * cur.x - lastCubicCtrl.x, y: 2 * cur.y - lastCubicCtrl.y }
            : cur;
        const rest = upper === "C" ? args.slice(2) : args;
        const c2 = pt(rest[0], rest[1]);
        const p = pt(rest[2], rest[3]);
        out.push({ type: "C", c1, c2, p });
        nextCubic = c2;
        cur = p;
        break;
      }
      case "Q":
      case "T": {
        const q =
          upper === "Q"
            ? pt(args[0], args[1])
            : lastQuadCtrl
            ? { x: 2 * cur.x - lastQuadCtrl.x, y: 2 * cur.y - lastQuadCtrl.y }
            : cur;
        const p = upper === "Q" ? pt(args[2], args[3]) : pt(args[0], args[1]);
        out.push(quadToCubic(cur, q, p));
        nextQuad = q;
        cur = p;
        break;
      }
      case "A": {
        const p = pt(args[5], args[6]);
        out.push(...arcToCubics(cur, args, p));
        cur = p;
        break;
      }
      case "Z":
        out.push({ type: "Z" });
        cur = start;
        break;
      default:
        break;
    }
    lastCubicCtrl = nextCubic;
    lastQuadCtrl = nextQuad;
  }
  // A path must start with a moveto to be valid.
  return out.length > 0 && out[0].type === "M" ? out : [];
};

export const transformPath = (segments: readonly Segment[], m: Matrix): Segment[] =>
  segments.map((s) => {
    switch (s.type) {
      case "Z":
        return s;
      case "C":
        return { type: "C", c1: apply(m, s.c1), c2: apply(m, s.c2), p: apply(m, s.p) };
      default:
        return { type: s.type, p: apply(m, s.p) };
    }
  });

// Control-point hull bounds: a safe superset of the exact curve bounds.
export const pathBounds = (segments: readonly Segment[]): Bounds | null => {
  const points: Point[] = [];
  segments.forEach((s) => {
    if (s.type === "C") points.push(s.c1, s.c2, s.p);
    else if (s.type !== "Z") points.push(s.p);
  });
  if (points.length === 0) return null;
  return {
    minX: Math.min(...points.map((p) => p.x)),
    minY: Math.min(...points.map((p) => p.y)),
    maxX: Math.max(...points.map((p) => p.x)),
    maxY: Math.max(...points.map((p) => p.y)),
  };
};

const fmt = (n: number): string => String(Math.round(n * 1000) / 1000);
const fmtPoint = (p: Point): string => `${fmt(p.x)} ${fmt(p.y)}`;

export const serializePath = (segments: readonly Segment[]): string =>
  segments
    .map((s) => {
      switch (s.type) {
        case "Z":
          return "Z";
        case "C":
          return `C${fmtPoint(s.c1)} ${fmtPoint(s.c2)} ${fmtPoint(s.p)}`;
        default:
          return `${s.type}${fmtPoint(s.p)}`;
      }
    })
    .join("");

const KAPPA = 0.5522847498;

export const ellipsePath = (cx: number, cy: number, rx: number, ry: number): Segment[] => {
  const kx = rx * KAPPA;
  const ky = ry * KAPPA;
  return [
    { type: "M", p: { x: cx + rx, y: cy } },
    { type: "C", c1: { x: cx + rx, y: cy + ky }, c2: { x: cx + kx, y: cy + ry }, p: { x: cx, y: cy + ry } },
    { type: "C", c1: { x: cx - kx, y: cy + ry }, c2: { x: cx - rx, y: cy + ky }, p: { x: cx - rx, y: cy } },
    { type: "C", c1: { x: cx - rx, y: cy - ky }, c2: { x: cx - kx, y: cy - ry }, p: { x: cx, y: cy - ry } },
    { type: "C", c1: { x: cx + kx, y: cy - ry }, c2: { x: cx + rx, y: cy - ky }, p: { x: cx + rx, y: cy } },
    { type: "Z" },
  ];
};

export const rectPath = (x: number, y: number, w: number, h: number, r: number): Segment[] => {
  if (r <= 0) {
    return [
      { type: "M", p: { x, y } },
      { type: "L", p: { x: x + w, y } },
      { type: "L", p: { x: x + w, y: y + h } },
      { type: "L", p: { x, y: y + h } },
      { type: "Z" },
    ];
  }
  const k = r * (1 - KAPPA);
  return [
    { type: "M", p: { x: x + r, y } },
    { type: "L", p: { x: x + w - r, y } },
    { type: "C", c1: { x: x + w - k, y }, c2: { x: x + w, y: y + k }, p: { x: x + w, y: y + r } },
    { type: "L", p: { x: x + w, y: y + h - r } },
    { type: "C", c1: { x: x + w, y: y + h - k }, c2: { x: x + w - k, y: y + h }, p: { x: x + w - r, y: y + h } },
    { type: "L", p: { x: x + r, y: y + h } },
    { type: "C", c1: { x: x + k, y: y + h }, c2: { x, y: y + h - k }, p: { x, y: y + h - r } },
    { type: "L", p: { x, y: y + r } },
    { type: "C", c1: { x, y: y + k }, c2: { x: x + k, y }, p: { x: x + r, y } },
    { type: "Z" },
  ];
};

export const polyPath = (points: readonly number[], closed: boolean): Segment[] => {
  const segments: Segment[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) {
    segments.push({ type: i === 0 ? "M" : "L", p: { x: points[i], y: points[i + 1] } });
  }
  if (closed && segments.length > 0) segments.push({ type: "Z" });
  return segments;
};
